/**
 * @file Uppy AwsS3 multipart plugin configuration for ZIP uploads.
 * Handles register/sign/complete lifecycle with persisted context resume.
 */

import AwsS3, {
  type AwsS3MultipartOptions,
  type AwsS3Part,
  type AwsS3UploadParameters,
} from '@uppy/aws-s3';
import type { UppyFile } from '@uppy/core';
import type Uppy from '@uppy/core';

import { isZipUppyFile } from './is-zip-uppy-file';

import type { PersistedMultipartContext } from '../types';

/** Default S3 multipart chunk size (6 MiB). */
export const CHUNK_SIZE_6MB = 6 * 1024 * 1024;

export type S3MultipartPartUrl = {
  upload_id?: string | null;
  part_number: number;
  url: string;
  etag?: string | null;
};

export type S3MultipartRegisterResponse = {
  upload_id?: string | null;
  file_name?: string | null;
  s3_upload_urls?: S3MultipartPartUrl[] | null;
};

export type S3MultipartFinalizePart = {
  PartNumber?: number | string;
  ETag?: string;
};

/** Register/finalize callbacks resolved per file by upload kind adapter. */
export type S3MultipartHandlers = {
  register: (input: {
    fileName: string;
    contentType: string;
    numberOfParts: number;
  }) => Promise<S3MultipartRegisterResponse>;
  finalize: (input: {
    fileName: string;
    uploadId: string;
    parts: S3MultipartFinalizePart[];
    partUrlStore: Record<number, string>;
  }) => Promise<unknown>;
};

/** In-memory presigned part URLs keyed by uploadId then part number. */
export type PartUrlStore = Record<string, Record<number, string>>;

/** Options for {@link configureS3MultipartPlugin}. */
export type CreateS3MultipartPluginOptions = {
  resolveHandlers: (file: UppyFile<Meta, Body>) => S3MultipartHandlers;
  partUrlStore: PartUrlStore;
  onRegister?: (context: PersistedMultipartContext) => void | Promise<void>;
  onFinalize?: (uppyFileId: string) => void | Promise<void>;
  getPersistedContext?: (
    uppyFileId: string
  ) => PersistedMultipartContext | undefined;
  shouldUseMultipart?: (file: UppyFile<Meta, Body>) => boolean;
};

type Meta = Record<string, unknown>;
type Body = Record<string, unknown>;

/**
 * Registers and configures the AwsS3 multipart plugin on a Uppy instance.
 * @param uppy - Uppy instance from {@link createUploadManagerUppy}.
 * @param options - Handler resolution, persistence hooks, and multipart eligibility.
 */
export const configureS3MultipartPlugin = (
  uppy: Uppy<Meta, Body>,
  options: CreateS3MultipartPluginOptions
) => {
  const {
    resolveHandlers,
    partUrlStore,
    onRegister,
    onFinalize,
    getPersistedContext,
    shouldUseMultipart = isZipUppyFile,
  } = options;

  uppy.use(AwsS3, {
    id: 'AwsS3',
    shouldUseMultipart,
    getChunkSize: () => CHUNK_SIZE_6MB,

    createMultipartUpload: async (file: UppyFile<Meta, Body>) => {
      if (!file.name || !file.size) {
        throw new Error('File name and size are required');
      }

      const persisted = getPersistedContext?.(file.id);
      if (persisted && persisted.uploadId) {
        partUrlStore[persisted.uploadId] = { ...persisted.partUrlStore };
        return {
          uploadId: persisted.uploadId,
          key: file.name,
        };
      }

      const handlers = resolveHandlers(file);
      const response = await handlers.register({
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
        numberOfParts: Math.max(1, Math.ceil(file.size / CHUNK_SIZE_6MB)),
      });

      const uploadId = response.upload_id;
      if (!uploadId || !response.s3_upload_urls?.[0]?.url) {
        throw new Error('Failed to initialize multipart upload');
      }

      partUrlStore[uploadId] = {};
      response.s3_upload_urls.forEach((urlInfo) => {
        partUrlStore[uploadId][urlInfo.part_number] = urlInfo.url;
      });

      const resourceKey =
        typeof file.meta?.resourceKey === 'string' ? file.meta.resourceKey : '';

      await onRegister?.({
        uppyFileId: file.id,
        uploadId,
        resourceKey,
        partUrlStore: { ...partUrlStore[uploadId] },
        registeredAt: new Date().toISOString(),
      });

      return {
        uploadId,
        key: response.file_name ?? file.name,
      };
    },

    listParts: async () => [],

    signPart: async (
      file: UppyFile<Meta, Body>,
      {
        uploadId,
        partNumber,
      }: {
        uploadId: string;
        key: string;
        partNumber: number;
        body: Blob;
        signal?: AbortSignal;
      }
    ) => {
      const url = partUrlStore[uploadId]?.[partNumber];
      if (!url) {
        throw new Error(`No pre-signed URL found for part ${partNumber}`);
      }

      return {
        url,
        headers: {
          'Content-Type': file.type || 'application/octet-stream',
        },
      } as AwsS3UploadParameters;
    },

    completeMultipartUpload: async (
      file: UppyFile<Meta, Body>,
      {
        uploadId,
        parts,
      }: {
        uploadId: string;
        key: string;
        parts: AwsS3Part[];
        signal: AbortSignal;
      }
    ) => {
      if (!file.name) {
        throw new Error('File name is required');
      }

      try {
        const handlers = resolveHandlers(file);
        await handlers.finalize({
          fileName: file.name,
          uploadId,
          parts,
          partUrlStore: partUrlStore[uploadId] ?? {},
        });

        if (uploadId in partUrlStore) {
          delete partUrlStore[uploadId];
        }

        await onFinalize?.(file.id);

        return { location: file.name };
      } catch (error) {
        if (uploadId in partUrlStore) {
          delete partUrlStore[uploadId];
        }
        throw error;
      }
    },

    abortMultipartUpload: async (
      _file: UppyFile<Meta, Body>,
      { uploadId }: { uploadId: string; key: string; signal?: AbortSignal }
    ) => {
      if (uploadId && uploadId in partUrlStore) {
        delete partUrlStore[uploadId];
      }
    },
  } as unknown as AwsS3MultipartOptions<Meta, Body>);
};

/**
 * Whether a browser File should use S3 multipart transport (ZIP archives).
 * @param file - Browser File from enqueue.
 */
export const isZipUploadFile = (file: File) =>
  file.name.toLowerCase().endsWith('.zip') ||
  file.type === 'application/zip' ||
  file.type === 'application/x-zip-compressed';
